import { useTranslation } from 'react-i18next';

const normalizeStatus = (status) => {
  if (status === 1) return 'confirmed';
  if (status === -1) return 'declined';
  if (status === 0) return 'pending';
  return typeof status === 'string' ? status : 'pending';
};

function ReactDialog({
  event,
  title,
  currentUser,
  giveawayStars,
  givenStars,
  existingPictures,
  loading = false,
  inline = false,
  onClose,
  onSendStar,
  onAddPicture,
  onUserClick,
}) {
  const { t } = useTranslation();

  const dialogTitle = title
    ? t('reactDialog.titleWithName', { name: title })
    : event
    ? t('reactDialog.titleWithName', { name: event.name })
    : t('reactDialog.defaultTitle');

  const isCurrentUserParticipant = event
    ? event.organizer.id === currentUser.id ||
      event.participants?.some(
        (p) => p.user.id === currentUser.id && normalizeStatus(p.status) === 'confirmed'
      )
    : false;

  const dialogContent = (
    <>
      <div className="dialog-header">
        <h4>{dialogTitle}</h4>
        {!inline && <button className="close-dialog" onClick={onClose}>×</button>}
      </div>

      {loading || !event ? (
        <p className="loading-text">{t('reactDialog.loading')}</p>
      ) : (
        <div className="participants-list">
          {giveawayStars === 0 && (
            <div className="no-stars-notice">
              {t('reactDialog.noStarsNotice')}
            </div>
          )}
          {giveawayStars === 1 && (
            <div className="last-star-notice">
              {t('reactDialog.lastStarNotice')}
            </div>
          )}

          {/* Organizer - only shown when current user is NOT the organizer */}
          {event.organizer.id !== currentUser.id && (() => {
            const hasExistingPicture = existingPictures[event.organizer.id] || false;
            const organizerAsParticipant = {
              id: `organizer-${event.organizer.id}`,
              user: event.organizer,
            };
            const canUploadToOrganizer = isCurrentUserParticipant;

            return (
              <div className="participant-item organizer">
                <div className="participant-info">
                  <button className="participant-name-btn" onClick={() => onUserClick?.(event.organizer.id)}>{event.organizer.displayName}</button>
                  <span className="participant-username">@{event.organizer.username}</span>
                </div>
                <div className="participant-actions">
                  <span className="participant-badge organizer-badge">{t('reactDialog.organizerBadge')}</span>
                  {giveawayStars > 0 && !givenStars[event.organizer.id] && (
                    <button
                      className="send-star-btn"
                      onClick={() => onSendStar(organizerAsParticipant)}
                      title={t('reactDialog.sendStarTitle')}
                    >
                      ⭐
                    </button>
                  )}
                  {givenStars[event.organizer.id] && (
                    <span className="star-given-indicator" title={t('reactDialog.starGivenTitle')}>⭐✓</span>
                  )}
                  {canUploadToOrganizer && onAddPicture && (
                    <button
                      className={`add-picture-btn ${hasExistingPicture ? 'disabled' : ''}`}
                      onClick={() => !hasExistingPicture && onAddPicture(organizerAsParticipant)}
                      disabled={hasExistingPicture}
                      title={hasExistingPicture ? t('reactDialog.pictureUploadedTitle') : t('reactDialog.addPictureTitle')}
                    >
                      {hasExistingPicture ? '✓' : '+'}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Participants (excluding organizer and current user) */}
          {event.participants &&
            event.participants
              .filter(
                (p) => p.user.id !== event.organizer.id && p.user.id !== currentUser.id
              )
              .map((participant) => {
                const status = normalizeStatus(participant.status);
                const isConfirmed = status === 'confirmed';
                const hasExistingPicture = existingPictures[participant.user.id] || false;
                const hasGivenStar = givenStars[participant.user.id] || false;
                const canUploadPicture = isCurrentUserParticipant && isConfirmed;

                return (
                  <div key={participant.id} className="participant-item">
                    <div className="participant-info">
                      <button className="participant-name-btn" onClick={() => onUserClick?.(participant.user.id)}>{participant.user.displayName}</button>
                      <span className="participant-username">@{participant.user.username}</span>
                    </div>
                    <div className="participant-actions">
                      <span className={`participant-badge status-${status}`}>
                        {status === 'confirmed' && t('reactDialog.statusConfirmed')}
                        {status === 'pending' && t('reactDialog.statusPending')}
                        {status === 'declined' && t('reactDialog.statusDeclined')}
                      </span>
                      {isConfirmed && giveawayStars > 0 && !hasGivenStar && (
                        <button
                          className="send-star-btn"
                          onClick={() => onSendStar(participant)}
                          title={t('reactDialog.sendStarTitle')}
                        >
                          ⭐
                        </button>
                      )}
                      {hasGivenStar && (
                        <span className="star-given-indicator" title={t('reactDialog.starGivenTitle')}>⭐✓</span>
                      )}
                      {canUploadPicture && onAddPicture && (
                        <button
                          className={`add-picture-btn ${hasExistingPicture ? 'disabled' : ''}`}
                          onClick={() => !hasExistingPicture && onAddPicture(participant)}
                          disabled={hasExistingPicture}
                          title={hasExistingPicture ? t('reactDialog.pictureUploadedTitle') : t('reactDialog.addPictureTitle')}
                        >
                          {hasExistingPicture ? '✓' : '+'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
        </div>
      )}
    </>
  );

  if (inline) {
    return (
      <div className="participants-dialog">
        {dialogContent}
      </div>
    );
  }

  return (
    <div className="participants-dialog-overlay" onClick={onClose}>
      <div className="participants-dialog" onClick={(e) => e.stopPropagation()}>
        {dialogContent}
      </div>
    </div>
  );
}

export default ReactDialog;
